import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload, DollarSign, Settings, Image as ImageIcon, Video,
  Package, LogOut, X, CheckCircle2, AlertCircle, Loader2, GripVertical
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../lib/api';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface StagedFile {
  id: string;
  file: File;
  preview: string; // object URL for local preview
  price: number;
  mediaType: 'image' | 'video';
}

interface UploadedPhoto {
  _id: string;
  filename: string;
  mediaType: 'image' | 'video';
  watermarkedUrl: string;
  price: number;
  beach: string;
  date: string;
  packId?: string;
}

interface PackRecord {
  _id: string;
  name: string;
  price: number;
  mediaCount: number;
  beach: string;
  date: string;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function PhotographerDashboard() {
  const { user, token, login, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'upload' | 'pricing' | 'profile'>('upload');

  // Auth form state
  const [signupForm, setSignupForm] = useState({ name: '', email: '', password: '' });
  const [isLogin, setIsLogin] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Upload state
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [beach, setBeach] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [wetsuitColor, setWetsuitColor] = useState('');
  const [boardType, setBoardType] = useState('');
  const [createPack, setCreatePack] = useState(false);
  const [packName, setPackName] = useState('');
  const [packPrice, setPackPrice] = useState('29.99');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<{ message: string; error?: boolean } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pricing tab state (fetched from server)
  const [myPhotos, setMyPhotos] = useState<UploadedPhoto[]>([]);
  const [myPacks, setMyPacks] = useState<PackRecord[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  /* ---------------------------------------------------------------- */
  /* Auth handler                                                      */
  /* ---------------------------------------------------------------- */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    try {
      const endpoint = isLogin ? apiUrl('/api/auth/login') : apiUrl('/api/auth/signup');
      const payload = isLogin
        ? { email: signupForm.email, password: signupForm.password }
        : { name: signupForm.name, email: signupForm.email, password: signupForm.password, role: 'PHOTOGRAPHER' };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setErrorMsg(data.error || 'Something went wrong'); return; }
      login(data.user, data.token);
    } catch {
      setErrorMsg('Failed to connect to server. Is it running?');
    }
  };

  /* ---------------------------------------------------------------- */
  /* File staging (drag & drop + browse)                               */
  /* ---------------------------------------------------------------- */

  const ACCEPTED_TYPES = [
    'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
    'video/mp4', 'video/quicktime', 'video/webm',
  ];

  const addFiles = useCallback((fileList: FileList | File[]) => {
    const newFiles: StagedFile[] = [];
    for (const file of Array.from(fileList)) {
      if (!ACCEPTED_TYPES.includes(file.type)) continue;
      newFiles.push({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
        price: 5,
        mediaType: file.type.startsWith('image/') ? 'image' : 'video',
      });
    }
    setStagedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const removeFile = (id: string) => {
    setStagedFiles(prev => {
      const removed = prev.find(f => f.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const updateFilePrice = (id: string, price: number) => {
    setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, price } : f));
  };

  // Drag-and-drop handlers
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  /* ---------------------------------------------------------------- */
  /* Upload to server                                                  */
  /* ---------------------------------------------------------------- */

  const handleUpload = async () => {
    if (stagedFiles.length === 0 || !beach || !date) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const formData = new FormData();
      stagedFiles.forEach(sf => formData.append('files', sf.file));
      formData.append('beach', beach);
      formData.append('date', date);
      formData.append('prices', JSON.stringify(stagedFiles.map(sf => sf.price)));
      if (wetsuitColor) formData.append('wetsuitColor', wetsuitColor);
      if (boardType) formData.append('boardType', boardType);
      if (createPack && packName) {
        formData.append('packName', packName);
        formData.append('packPrice', packPrice);
      }

      // Use XMLHttpRequest for upload progress
      const result = await new Promise<{ ok: boolean; data: Record<string, unknown> }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', apiUrl('/api/upload'));
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
          } catch {
            reject(new Error('Invalid response'));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      if (result.ok) {
        setUploadResult({ message: result.data.message as string });
        // Clean up previews and reset
        stagedFiles.forEach(sf => URL.revokeObjectURL(sf.preview));
        setStagedFiles([]);
        setCreatePack(false);
        setPackName('');
      } else {
        setUploadResult({ message: (result.data.error as string) || 'Upload failed', error: true });
      }
    } catch (err) {
      setUploadResult({ message: 'Upload failed. Is the server running?', error: true });
    } finally {
      setUploading(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /* Fetch existing photos/packs for Pricing tab                       */
  /* ---------------------------------------------------------------- */

  const fetchMyAssets = useCallback(async () => {
    if (!token) return;
    setLoadingAssets(true);
    try {
      const res = await fetch(apiUrl('/api/photos/my'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMyPhotos(data.photos || []);
        setMyPacks(data.packs || []);
      }
    } catch {
      // silent
    } finally {
      setLoadingAssets(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'pricing' && token) {
      fetchMyAssets();
    }
  }, [activeTab, token, fetchMyAssets]);

  /* ---------------------------------------------------------------- */
  /* Inline price update handlers                                      */
  /* ---------------------------------------------------------------- */

  const savePhotoPrice = async (photoId: string, price: number) => {
    await fetch(apiUrl(`/api/photos/${photoId}/price`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ price }),
    });
  };

  const savePackPrice = async (packId: string, price: number) => {
    await fetch(apiUrl(`/api/photos/pack/${packId}/price`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ price }),
    });
  };

  /* ---------------------------------------------------------------- */
  /* Render: Unauthenticated / non-photographer                        */
  /* ---------------------------------------------------------------- */

  if (!user || user.role !== 'PHOTOGRAPHER') {
    return (
      <div className="max-w-md mx-auto mt-12 bg-white p-8 rounded-2xl shadow-sm border border-slate-200">
        <h2 className="text-2xl font-bold mb-2">Photographer {isLogin ? 'Login' : 'Signup'}</h2>
        <p className="text-slate-500 mb-6">
          {isLogin ? 'Welcome back! Sign in to access your dashboard.' : 'Create an account to start selling your surf photos.'}
        </p>

        {errorMsg && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">{errorMsg}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
              <input required={!isLogin} type="text" value={signupForm.name}
                onChange={e => setSignupForm({ ...signupForm, name: e.target.value })}
                className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input required type="email" value={signupForm.email}
              onChange={e => setSignupForm({ ...signupForm, email: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input required type="password" value={signupForm.password}
              onChange={e => setSignupForm({ ...signupForm, password: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <button type="submit" className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors mt-4">
            {isLogin ? 'Sign In' : 'Sign Up & Access Dashboard'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button type="button" onClick={() => { setIsLogin(!isLogin); setErrorMsg(''); }}
            className="text-sm text-blue-600 hover:underline">
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Log in"}
          </button>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Render: Authenticated photographer dashboard                      */
  /* ---------------------------------------------------------------- */

  return (
    <div className="flex flex-col md:flex-row gap-8 min-h-[calc(100vh-12rem)]">
      {/* Sidebar */}
      <aside className="w-full md:w-64 flex flex-col gap-2">
        <div className="px-4 py-4 mb-2 bg-slate-900 text-white rounded-xl">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Welcome Back</p>
          <p className="font-medium truncate">{user.name}</p>
        </div>

        {([
          { key: 'upload' as const, icon: Upload, label: 'Upload Media' },
          { key: 'pricing' as const, icon: DollarSign, label: 'Pricing & Packs' },
          { key: 'profile' as const, icon: Settings, label: 'Profile & Payouts' },
        ]).map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`text-left px-4 py-3 rounded-lg flex items-center gap-3 font-medium transition-colors ${activeTab === tab.key ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            <tab.icon className="h-5 w-5" />
            {tab.label}
          </button>
        ))}

        <div className="mt-auto pt-4 border-t border-slate-200">
          <button onClick={logout}
            className="w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 font-medium text-red-600 hover:bg-red-50 transition-colors">
            <LogOut className="h-5 w-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 bg-white p-8 rounded-2xl shadow-sm border border-slate-200">

        {/* ==================== UPLOAD TAB ==================== */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Upload Photos & Videos</h2>
              <p className="text-slate-500">Drag & drop or browse files. Set per-file prices and optionally bundle as a pack.</p>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-12 flex flex-col items-center justify-center text-center transition-colors cursor-pointer
                ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}
            >
              <div className="flex gap-4 mb-4 text-slate-400">
                <ImageIcon className="h-10 w-10" />
                <Video className="h-10 w-10" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">
                {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
              </h3>
              <p className="text-sm text-slate-500 mb-6">Supports JPG, PNG, WEBP, HEIC, MP4, MOV, WEBM up to 500MB</p>
              <span className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800 inline-block">
                Browse Files
              </span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
                className="hidden"
                onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
              />
            </div>

            {/* Staged file list with per-file pricing */}
            {stagedFiles.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700">
                  {stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} staged
                </h3>

                <div className="grid gap-3">
                  {stagedFiles.map(sf => (
                    <div key={sf.id}
                      className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />

                      {/* Thumbnail */}
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 shrink-0">
                        {sf.mediaType === 'image' ? (
                          <img src={sf.preview} alt={sf.file.name} className="w-full h-full object-cover" />
                        ) : (
                          <video src={sf.preview} className="w-full h-full object-cover" />
                        )}
                      </div>

                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{sf.file.name}</p>
                        <p className="text-xs text-slate-500">
                          {sf.mediaType === 'image' ? <ImageIcon className="h-3 w-3 inline mr-1" /> : <Video className="h-3 w-3 inline mr-1" />}
                          {(sf.file.size / (1024 * 1024)).toFixed(1)} MB
                        </p>
                      </div>

                      {/* Per-file price */}
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-slate-500 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={sf.price}
                          onChange={e => updateFilePrice(sf.id, parseFloat(e.target.value) || 0)}
                          className="w-20 p-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>

                      {/* Remove button */}
                      <button onClick={() => removeFile(sf.id)} className="p-1 text-slate-400 hover:text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Session metadata */}
                <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Beach *</label>
                    <input type="text" required value={beach} onChange={e => setBeach(e.target.value)}
                      placeholder="e.g. Pipeline, Huntington"
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                    <input type="date" required value={date} onChange={e => setDate(e.target.value)}
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Wetsuit Color</label>
                    <input type="text" value={wetsuitColor} onChange={e => setWetsuitColor(e.target.value)}
                      placeholder="e.g. Black, Red"
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Board Type</label>
                    <input type="text" value={boardType} onChange={e => setBoardType(e.target.value)}
                      placeholder="e.g. Shortboard, Longboard"
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                </div>

                {/* Pack option */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={createPack} onChange={e => setCreatePack(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                    <div>
                      <span className="font-medium text-slate-700 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Also sell as a Session Pack
                      </span>
                      <span className="text-xs text-slate-500">Bundle all {stagedFiles.length} files at a discounted pack price</span>
                    </div>
                  </label>

                  {createPack && (
                    <div className="grid md:grid-cols-2 gap-4 mt-4 pl-7">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Pack Name</label>
                        <input type="text" value={packName} onChange={e => setPackName(e.target.value)}
                          placeholder="e.g. Pipeline Morning Session"
                          className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Pack Price</label>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500">$</span>
                          <input type="number" min="0" step="0.01" value={packPrice}
                            onChange={e => setPackPrice(e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload progress & button */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading... {uploadProgress}%
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}

                {uploadResult && (
                  <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${uploadResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    {uploadResult.error ? <AlertCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    {uploadResult.message}
                  </div>
                )}

                <button
                  onClick={handleUpload}
                  disabled={uploading || !beach || !date}
                  className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload {stagedFiles.length} File{stagedFiles.length > 1 ? 's' : ''}
                      {createPack ? ' + Create Pack' : ''}
                    </>
                  )}
                </button>
              </div>
            )}

            {stagedFiles.length === 0 && !uploadResult && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-blue-800 text-sm flex items-start gap-3">
                <div className="mt-0.5">i</div>
                <p>Drag photos and videos into the zone above. You can set individual prices for each file, and optionally bundle them as a session pack.</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== PRICING TAB ==================== */}
        {activeTab === 'pricing' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold mb-2">Pricing & Packs</h2>
              <p className="text-slate-500">Manage individual photo prices and session pack pricing for your uploads.</p>
            </div>

            {loadingAssets ? (
              <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading your uploads...
              </div>
            ) : (
              <>
                {/* Packs section */}
                {myPacks.length > 0 && (
                  <div>
                    <h3 className="font-bold flex items-center gap-2 mb-4">
                      <Package className="h-5 w-5 text-slate-400" />
                      Session Packs
                    </h3>
                    <div className="grid gap-3">
                      {myPacks.map(pack => (
                        <div key={pack._id} className="flex items-center gap-4 p-4 border border-slate-200 rounded-xl">
                          <div className="flex-1">
                            <p className="font-medium">{pack.name}</p>
                            <p className="text-xs text-slate-500">{pack.beach} &middot; {new Date(pack.date).toLocaleDateString()} &middot; {pack.mediaCount} files</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500 text-sm">$</span>
                            <input
                              type="number" min="0" step="0.01"
                              defaultValue={pack.price}
                              onBlur={e => savePackPrice(pack._id, parseFloat(e.target.value) || 0)}
                              className="w-24 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Individual photos section */}
                {myPhotos.length > 0 ? (
                  <div>
                    <h3 className="font-bold flex items-center gap-2 mb-4">
                      <ImageIcon className="h-5 w-5 text-slate-400" />
                      Individual Media ({myPhotos.length})
                    </h3>
                    <div className="grid gap-3">
                      {myPhotos.map(photo => (
                        <div key={photo._id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          {/* Thumbnail */}
                          <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-200 shrink-0">
                            {photo.mediaType === 'image' ? (
                              <img src={photo.watermarkedUrl} alt={photo.filename} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Video className="h-6 w-6 text-slate-400" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{photo.filename}</p>
                            <p className="text-xs text-slate-500">{photo.beach} &middot; {new Date(photo.date).toLocaleDateString()}</p>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-slate-500 text-sm">$</span>
                            <input
                              type="number" min="0" step="0.01"
                              defaultValue={photo.price}
                              onBlur={e => savePhotoPrice(photo._id, parseFloat(e.target.value) || 0)}
                              className="w-20 p-1.5 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <ImageIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No uploads yet</p>
                    <p className="text-sm">Upload photos in the Upload tab to set pricing here.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== PROFILE TAB ==================== */}
        {activeTab === 'profile' && (
          <div className="space-y-8 max-w-2xl">
            <div>
              <h2 className="text-2xl font-bold mb-2">Profile & Payouts</h2>
              <p className="text-slate-500">Provide your details to ensure you get paid for your sales.</p>
            </div>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                  <input type="text" className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                  <input type="text" className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                <input type="email" defaultValue={user.email}
                  className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                <input type="tel" className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>

              <div className="pt-6 border-t border-slate-200">
                <h3 className="font-bold mb-4">Bank Details (For Payouts)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Bank Name</label>
                    <input type="text" className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Account Number / IBAN</label>
                    <input type="text" className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                </div>
              </div>

              <button className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700">
                Save Profile
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
